"use client";

import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { transformStatesCitiesData } from "@/utils/statesCitiesTransform";

interface City {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
}

interface State {
  id: number;
  name: string;
  state_code: string;
  latitude: string;
  longitude: string;
  type: string;
  cities: City[];
}

interface StateCitySelectorProps {
  selectedState?: string;
  selectedCity?: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  stateError?: string;
  cityError?: string;
  stateHasError?: boolean;
  cityHasError?: boolean;
  required?: boolean;
}

export const StateCitySelector: React.FC<StateCitySelectorProps> = ({
  selectedState = "",
  selectedCity = "",
  onStateChange,
  onCityChange,
  stateError,
  cityError,
  stateHasError = false,
  cityHasError = false,
  required = false,
}) => {
  const [states, setStates] = useState<State[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(true);

  // Load states data
  useEffect(() => {
    const loadStates = async () => {
      try {
        const response = await fetch("/states-cities.json");
        const rawData: Record<string, string[]> = await response.json();
        const transformedData = transformStatesCitiesData(rawData);
        setStates(transformedData);
      } catch (error) {
        console.error("Failed to load states data:", error);
      } finally {
        setIsLoadingStates(false);
      }
    };

    loadStates();
  }, []);

  // Update cities when state changes
  useEffect(() => {
    if (selectedState && states.length > 0) {
      const state = states.find((s) => s.name === selectedState);
      if (state) {
        setAvailableCities(state.cities);
      } else {
        setAvailableCities([]);
      }
    } else {
      setAvailableCities([]);
    }

    // Clear city selection when state changes
    if (selectedCity && selectedState) {
      const state = states.find((s) => s.name === selectedState);
      if (state && !state.cities.find((c) => c.name === selectedCity)) {
        onCityChange("");
      }
    }
  }, [selectedState, states, selectedCity, onCityChange]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value;
    onStateChange(newState);
    onCityChange(""); // Clear city when state changes
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onCityChange(e.target.value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* State Selector */}
      <div>
        <label className="block text-[13px] font-normal mb-1">
          State <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <select
            value={selectedState}
            onChange={handleStateChange}
            disabled={isLoadingStates}
            aria-required={required}
            className={`w-full bg-background border rounded-xl h-[50px] px-4 py-2 appearance-none cursor-pointer text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
              stateHasError
                ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                : "border-border hover:border-primary/50"
            } ${isLoadingStates ? "opacity-50" : ""}`}
            style={{
              colorScheme: "dark",
            }}
          >
            <option value="" className="bg-background text-muted-foreground">
              {isLoadingStates ? "Loading states..." : "Select State"}
            </option>
            {states.map((state) => (
              <option
                key={state.id}
                value={state.name}
                className="bg-background text-foreground"
              >
                {state.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
        {stateError && (
          <p className="text-destructive text-sm mt-1">{stateError}</p>
        )}
      </div>

      {/* City Selector */}
      <div>
        <label className="block text-[13px] font-normal mb-1">
          City <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <select
            value={selectedCity}
            onChange={handleCityChange}
            disabled={!selectedState || availableCities.length === 0}
            aria-required={required}
            className={`w-full bg-background border rounded-xl h-[50px] px-4 py-2 appearance-none cursor-pointer text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${
              cityHasError
                ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                : "border-border hover:border-primary/50"
            } ${!selectedState ? "opacity-50" : ""}`}
            style={{
              colorScheme: "dark",
            }}
          >
            <option value="" className="bg-background text-muted-foreground">
              {!selectedState
                ? "Select state first"
                : availableCities.length === 0
                  ? "No cities available"
                  : "Select City"}
            </option>
            {availableCities.map((city) => (
              <option
                key={city.id}
                value={city.name}
                className="bg-background text-foreground"
              >
                {city.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
        {cityError && (
          <p className="text-destructive text-sm mt-1">{cityError}</p>
        )}
        {!selectedState && (
          <p className="text-muted-foreground text-xs mt-1">
            Please select a state first to see available cities
          </p>
        )}
      </div>
    </div>
  );
};

export default StateCitySelector;
